import { motion } from "framer-motion";
import type { SquadCreationBlockedResponse } from "@workspace/api-client-react";

// WI#13 (slice B3) — the consumer for the 402 that POST /api/squads has returned since WI#6
// slice 2. Until this existed the mutation had no onError at all, so a blocked create was a
// silent no-op: the modal just sat there.
//
// Deliberately NOT a generic "payment required" dialog — the two codes are different user
// situations and need different asks. `subscription_required` is someone who has never paid;
// `plan_limit_reached` is a paying Squad subscriber who wants a second squad.
const COPY: Record<
  SquadCreationBlockedResponse["code"],
  { title: string; cta: string }
> = {
  subscription_required: {
    title: "Creating a squad is a paid feature",
    cta: "See plans",
  },
  plan_limit_reached: {
    title: "You've used the squad your plan includes",
    cta: "Upgrade to Coach Pro",
  },
};

export function UpgradePrompt({
  blocked,
  onClose,
}: {
  blocked: SquadCreationBlockedResponse;
  onClose: () => void;
}) {
  const copy = COPY[blocked.code] ?? COPY.subscription_required;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="pr-modal"
      >
        <h2 className="font-display text-xl font-bold mb-2">{copy.title}</h2>
        {/* Server-authored message — it knows the actual plan and limit, so it stays the
            source of truth rather than being restated here and drifting. */}
        <p className="text-muted-foreground text-sm mb-6">{blocked.error}</p>

        {blocked.checkoutUrl ? (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Not now
            </button>
            <a
              href={blocked.checkoutUrl}
              className="flex-1 bg-primary text-primary-foreground rounded py-2 text-sm font-semibold hover:opacity-90 transition-opacity text-center"
            >
              {copy.cta}
            </a>
          </div>
        ) : (
          // checkoutUrl is omitted when PEAKCOMMERCE_CHECKOUT_URL is unset. The server already
          // logs that as an error; surface it honestly here rather than rendering a dead button
          // that looks like it works.
          <>
            <p className="text-destructive text-sm mb-6">
              Checkout isn't available right now. This is on our side, not yours — please try
              again shortly.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="w-full border border-border rounded py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Close
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
